/**
 * Theme Storage Service
 *
 * Manages persistence of theme preference using IndexedDB.
 *
 * WHY IndexedDB instead of localStorage:
 * - Asynchronous (doesn't block the main thread)
 * - Larger storage capacity (no 5-10MB limit like localStorage)
 * - Better handling of structured data
 * - More suitable for modern web applications
 * - IndexedDB is the recommended storage API for persistent data in web apps
 * - Works reliably in private browsing mode (limited storage but functional)
 *
 * USE CASE:
 * This allows the application to remember theme preference even when:
 * - The backend server is down temporarily
 * - Network errors occur during preference loading
 * - The user hasn't authenticated yet (non-authenticated theme preference)
 *
 * NOTE: For authenticated users, theme preference is stored in the backend database.
 * This IndexedDB storage serves as a fallback/cache for non-authenticated users
 * and for quick theme loading without waiting for API calls.
 */

// IndexedDB is a standard browser API, eslint-disable is appropriate here
/* eslint-disable no-undef */

/**
 * Database configuration
 */
const DB_NAME = 'HarmonyThemeDB';
const DB_VERSION = 1;
const STORE_NAME = 'themePreference';

/**
 * Theme preference record stored in IndexedDB
 */
interface ThemePreferenceRecord {
  theme: 'auto' | 'light' | 'dark';
  lastUpdated: number; // timestamp in milliseconds
}

/**
 * ThemeStorage Service
 *
 * Provides methods to persist and retrieve theme preference.
 */
class ThemeStorageService {
  private db: IDBDatabase | null = null;

  /**
   * Initialize IndexedDB database and create themePreference store
   *
   * Creates a new IndexedDB database if it doesn't exist,
   * with a single object store for theme preference.
   *
   * @returns Promise<void>
   */
  private async initializeDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB for theme storage'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(request.result);
      };

      // Create object store only when database is first created or version changes
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store with 'id' as key path (single-record design)
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('theme', 'theme', { unique: false });
        }
      };
    });
  }

  /**
   * Get database instance, initializing if necessary
   *
   * @returns Promise<IDBDatabase>
   */
  private async getDB(): Promise<IDBDatabase> {
    if (!this.db) {
      this.db = await this.initializeDB();
    }
    return this.db;
  }

  /**
   * Store theme preference in IndexedDB
   *
   * Called when user toggles theme via header (non-authenticated users)
   * or settings page (authenticated users) as a fallback cache.
   *
   * @param theme - Theme mode ('auto', 'light' or 'dark')
   * @returns Promise<void>
   */
  async setTheme(theme: 'auto' | 'light' | 'dark'): Promise<void> {
    try {
      const db = await this.getDB();
      const store = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME);

      const record: ThemePreferenceRecord & { id: string } = {
        id: 'current',
        theme,
        lastUpdated: Date.now(),
      };

      const request = store.put(record);

      await new Promise<void>((resolve, reject) => {
        request.onsuccess = () => resolve();
        request.onerror = () => reject(new Error('Failed to store theme preference'));
      });
    } catch (error) {
      console.warn('Failed to persist theme preference:', error);
      // Non-blocking: UX degrades gracefully if storage fails
    }
  }

  /**
   * Get stored theme preference from IndexedDB
   *
   * Returns the cached theme preference without making a network request.
   * Useful for:
   * - Non-authenticated users who want to keep theme preference
   * - Quick theme initialization before API call completes
   * - Fallback when backend is unavailable
   *
   * @returns Promise<'auto' | 'light' | 'dark' | null> - stored theme or null if not set
   */
  async getTheme(): Promise<'auto' | 'light' | 'dark' | null> {
    try {
      const db = await this.getDB();
      const store = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME);

      const request = store.get('current');

      const result = await new Promise<(ThemePreferenceRecord & { id: string }) | undefined>(
        (resolve, reject) => {
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(new Error('Failed to read theme preference'));
        },
      );

      return result?.theme ?? null;
    } catch (error) {
      console.warn('Failed to read theme preference:', error);
      // Fallback: assume no preference stored on storage errors
      return null;
    }
  }

  /**
   * Clear theme preference from IndexedDB
   *
   * Called when user logs out or explicitly clears local data.
   *
   * @returns Promise<void>
   */
  async clearTheme(): Promise<void> {
    try {
      const db = await this.getDB();
      const store = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME);

      const request = store.delete('current');

      await new Promise<void>((resolve, reject) => {
        request.onsuccess = () => resolve();
        request.onerror = () => reject(new Error('Failed to clear theme preference'));
      });
    } catch (error) {
      console.warn('Failed to clear theme preference:', error);
      // Non-blocking: UX degrades gracefully if storage fails
    }
  }

  /**
   * Clear all theme state (utility method for testing/cleanup)
   *
   * @returns Promise<void>
   */
  async clearAll(): Promise<void> {
    try {
      const db = await this.getDB();
      const store = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME);

      const request = store.clear();

      await new Promise<void>((resolve, reject) => {
        request.onsuccess = () => resolve();
        request.onerror = () => reject(new Error('Failed to clear all theme state'));
      });
    } catch (error) {
      console.warn('Failed to clear all theme state:', error);
    }
  }
}

// Export singleton instance
export const themeStorage = new ThemeStorageService();

/* eslint-enable no-undef */
